<template>
    <transition name="vr-modal-fade">
        <base-modal
            v-if="visible"
            :width="width"
            :height="height"
            :fixed-footer="fixedFooter"
            :fullscreen="maximized"
            :zoom-animation="zoomAnim"
            @close="handleClose"
        >
            <template v-if="allowMaximize" #headerActions>
                <button
                    type="button"
                    class="base-modal-control-btn base-modal-control-btn--expand vr-modal-expand-btn"
                    :aria-label="maximized ? 'Exit full screen' : 'Full screen'"
                    @click="toggleMaximize"
                >
                    <font-awesome-icon :icon="['fas', maximized ? 'compress' : 'expand']" />
                </button>
            </template>

            <template #header>
                <div
                    class="vr-modal-header vr-modal-header--with-controls"
                    :class="{ 'vr-modal-header--fullscreen': maximized }"
                >
                    <div class="vr-modal-header-inner">
                        <div class="vr-modal-header-icon-wrap">
                            <font-awesome-icon :icon="icon" class="vr-modal-header-icon" />
                        </div>
                        <div class="text-left min-w-0">
                            <h2 class="vr-modal-header-title">{{ title }}</h2>
                            <p v-if="subtitle" class="vr-modal-header-sub">{{ subtitle }}</p>
                        </div>
                    </div>
                </div>
            </template>

            <div class="vr-modal-shell">
                <div
                    class="vr-modal-scroll"
                    :class="{
                        'vr-modal-scroll--flush': flushBody,
                        'vr-modal-scroll--maximized': maximized,
                    }"
                >
                    <slot></slot>
                </div>
                <div
                    v-if="$slots.footer"
                    class="vr-modal-footer"
                    :class="{ 'vr-modal-footer--fullscreen': maximized }"
                >
                    <slot name="footer"></slot>
                </div>
            </div>
        </base-modal>
    </transition>
</template>

<script>
export default {
    name: 'VolunteeringStyledModal',
    emits: ['close'],
    props: {
        visible: { type: Boolean, default: false },
        title: { type: String, required: true },
        subtitle: { type: String, default: '' },
        icon: {
            type: Array,
            default: function () {
                return ['fas', 'hands-holding-heart'];
            },
        },
        width: { type: String, default: '720px' },
        height: { type: String, default: '' },
        fixedFooter: { type: Boolean, default: true },
        flushBody: { type: Boolean, default: false },
        allowMaximize: { type: Boolean, default: true },
    },
    data() {
        return {
            maximized: false,
            zoomAnim: '',
            zoomTimer: null,
        };
    },
    watch: {
        visible(val) {
            if (!val) {
                this.resetMaximizeState();
            }
        },
    },
    beforeUnmount() {
        this.clearZoomTimer();
    },
    methods: {
        handleClose() {
            this.resetMaximizeState();
            this.$emit('close');
        },
        clearZoomTimer() {
            if (this.zoomTimer) {
                clearTimeout(this.zoomTimer);
                this.zoomTimer = null;
            }
        },
        resetMaximizeState() {
            this.clearZoomTimer();
            this.maximized = false;
            this.zoomAnim = '';
        },
        toggleMaximize() {
            var self = this;
            var goingFullscreen = !this.maximized;
            this.zoomAnim = '';
            this.clearZoomTimer();
            this.$nextTick(function () {
                self.maximized = goingFullscreen;
                self.zoomAnim = goingFullscreen ? 'in' : 'out';
                self.zoomTimer = setTimeout(function () {
                    self.zoomAnim = '';
                    self.zoomTimer = null;
                }, 460);
            });
        },
    },
};
</script>

<style scoped>
.vr-modal-fade-enter-active,
.vr-modal-fade-leave-active {
    transition: opacity 0.2s ease;
}

.vr-modal-fade-enter-from,
.vr-modal-fade-leave-to {
    opacity: 0;
}

:deep(.base-modal-control-btn.vr-modal-expand-btn:hover),
:deep(.base-modal-control-btn.vr-modal-expand-btn:focus-visible) {
    background: #2563eb;
    border-color: #2563eb;
    color: #fff;
    box-shadow: 0 2px 8px rgba(37, 99, 235, 0.35);
}

.vr-modal-header {
    padding: 1.25rem 3rem 1.25rem 1.5rem;
    background: linear-gradient(135deg, #1e3a8a 0%, #2563eb 45%, #3b82f6 100%);
    color: #fff;
    border-radius: 1rem 1rem 0 0;
    box-shadow: inset 0 -1px 0 rgba(255, 255, 255, 0.08);
}

.vr-modal-header--with-controls {
    padding-right: 5.5rem;
}

.vr-modal-header--fullscreen {
    border-radius: 0;
}

.vr-modal-header-inner {
    display: flex;
    align-items: center;
    gap: 0.875rem;
}

.vr-modal-header-icon-wrap {
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 0.75rem;
    background: rgba(255, 255, 255, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.2);
}

.vr-modal-header-icon {
    font-size: 1.35rem;
    opacity: 0.95;
}

.vr-modal-header-title {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1.3;
}

.vr-modal-header-sub {
    margin: 0.2rem 0 0;
    font-size: 0.8125rem;
    color: rgba(219, 234, 254, 0.95);
    line-height: 1.35;
}

.vr-modal-shell {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
}

.vr-modal-scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 1.5rem;
    background: linear-gradient(180deg, #f8fafc 0%, #eff6ff 100%);
}

.vr-modal-scroll--flush {
    padding: 0;
}

.vr-modal-scroll--maximized {
    padding-bottom: 1.75rem;
}

.vr-modal-footer {
    flex-shrink: 0;
    padding: 1rem 1.5rem;
    border-top: 1px solid #e2e8f0;
    background: #fff;
}

.vr-modal-footer--fullscreen {
    border-radius: 0;
}
</style>
